import type { FormSchemaVersion } from './types';

/** Sentinel category key for the hardcoded Active Diagnosis section. */
export const ACTIVE_DIAGNOSIS_CATEGORY = '__ACTIVE_DIAGNOSIS__';

/** Sentinel category key for the hardcoded Goals and Interventions section. */
export const GOALS_CATEGORY = '__GOALS__';

/** Sentinel category key for the hardcoded Events (falls and infections) section. */
export const EVENTS_CATEGORY = '__EVENTS__';

/**
 * Sentinel pages for clinical modules on a multi-section form that has no
 * section to host them (see `MEDICATIONS_SECTION_ID` / `SKIN_SECTION_ID`).
 */
export const MEDICATIONS_CATEGORY = '__MEDICATIONS__';
export const WOUNDS_CATEGORY = '__WOUNDS__';
export const DIAGNOSIS_REVIEW_CATEGORY = '__DIAGNOSIS_REVIEW__';

/**
 * Canonical OASIS-E2 section ids that host embedded clinical modules
 * (wounds under Skin Conditions, meds under Medications). Shared with
 * contradiction jump-to so scrubber chips can switch sections.
 */
export const SKIN_SECTION_ID = 'Skin_Conditions';
export const MEDICATIONS_SECTION_ID = 'Medications';

const fallbackCategoryLabel = (category: string): string =>
  category
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim();

/** Build a `section_id → display_name` lookup from the FormSchemaVersion's section_order. */
export const buildLabelByCategory = (
  version: FormSchemaVersion | null | undefined
): Record<string, string> => {
  const lookup: Record<string, string> = {};
  for (const item of version?.section_order ?? []) {
    if (item?.section_id && item.display_name) {
      lookup[item.section_id] = item.display_name;
    }
  }
  return lookup;
};

export const resolveCategoryLabel = (
  category: string,
  labelByCategory: Record<string, string>
): string => {
  if (category === ACTIVE_DIAGNOSIS_CATEGORY) return 'Active Diagnosis';
  if (category === GOALS_CATEGORY) return 'Goals & Interventions';
  if (category === EVENTS_CATEGORY) return 'Events';
  if (category === MEDICATIONS_CATEGORY) return 'Medications';
  if (category === WOUNDS_CATEGORY) return 'Wounds';
  if (category === DIAGNOSIS_REVIEW_CATEGORY) return 'Diagnoses';
  return labelByCategory[category] ?? fallbackCategoryLabel(category);
};
